import React, { useState, useRef } from 'react';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import { SettingsFormData, SkillValidationError } from 'talent_matching/types';
import CalibrationForm from './calibration_form';
import { useCreateCalibrationSettings } from 'talent_matching/api';
import { useCalibrationModal } from 'talent_matching/contexts/calibration_modal_context';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { trackUserAction } from 'talent_matching/lib/tracker';
import { withLocalePrefix } from 'shared/utils/translation';

interface ValidationErrorResponse {
  error_type: string;
  invalid_skills: SkillValidationError[];
}

interface ErrorResponse {
  status: number;
  data: ValidationErrorResponse;
}

interface AxiosLikeError {
  response: ErrorResponse;
}

function hasResponseProperty(error: unknown): error is { response: unknown } {
  return error !== null && typeof error === 'object' && 'response' in error;
}

function hasValidResponseStructure(
  response: unknown
): response is ErrorResponse {
  return (
    response !== null &&
    typeof response === 'object' &&
    'status' in response &&
    'data' in response &&
    typeof (response as Record<string, unknown>).status === 'number' &&
    typeof (response as Record<string, unknown>).data === 'object' &&
    (response as Record<string, unknown>).data !== null
  );
}

function hasErrorType(data: unknown): data is ValidationErrorResponse {
  return data !== null && typeof data === 'object' && 'error_type' in data;
}

function isValidationError(error: unknown): error is AxiosLikeError {
  if (!hasResponseProperty(error)) {
    return false;
  }

  if (!hasValidResponseStructure(error.response)) {
    return false;
  }

  return hasErrorType(error.response.data);
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const t = withLocalePrefix('talent_matching.modals.calibration');

export const EMPTY_FORM_STATE: SettingsFormData = {
  use_boolean_search: false,
  min_years_experience: '',
  job_titles: [],
  skills: [],
  jobPostSkills: [],
  scorecardSkills: [],
  pipelineSkills: [],
  dev_only_all_apps: false,
  prioritize_current_experience: false,
  extracted_skills: [],
  industries: [],
};

const CalibrationModal = ({ isOpen, onClose }: Props) => {
  const { paths, jobTitle } = useCalibrationModal();
  const { organizationId, userId, hiringPlanId } = useUserContext();
  const [validationErrors, setValidationErrors] = useState<
    SkillValidationError[]
  >([]);
  const warnedSkillNamesRef = useRef<string[]>([]);

  const { mutate: createCalibrationSettings, isPending } =
    useCreateCalibrationSettings(paths);

  const onSubmit = async (data: SettingsFormData) => {
    validationErrors.forEach((error) => {
      const skillName = error.skill || '';
      if (skillName && !warnedSkillNamesRef.current.includes(skillName)) {
        warnedSkillNamesRef.current.push(skillName);
      }
    });

    setValidationErrors([]);

    createCalibrationSettings(
      {
        settings: {
          job_titles: data.job_titles,
          min_years_experience: data.min_years_experience ?? '',
          use_boolean_search: data.use_boolean_search,
          skills: data.skills,
          dev_only_all_apps: data.dev_only_all_apps,
          warned_skill_names: warnedSkillNamesRef.current,
          prioritize_current_experience: data.prioritize_current_experience,
          industries: data.industries,
        },
        errorMessage: t('save_error'),
      },
      {
        onSuccess: () => {
          trackUserAction({
            source: 'tm_app_review',
            organizationId,
            userId,
            hiringPlanId,
            category: 'action',
            action: 'calibration_saved',
          });
          const url = new URL(window.location.href);
          url.searchParams.set(
            'dev_only_all_apps',
            String(data.dev_only_all_apps)
          );
          window.history.replaceState({}, '', url.toString());
          onClose();
        },
        onError: (error: unknown) => {
          if (
            isValidationError(error) &&
            error.response.status === 422 &&
            error.response.data.error_type === 'invalid_skills'
          ) {
            const invalidSkills = error.response.data.invalid_skills || [];
            setValidationErrors(invalidSkills);
          } else {
            // eslint-disable-next-line no-console
            console.error('Calibration settings error:', error);
          }
        },
      }
    );
  };

  const handleOnClose = () => {
    setValidationErrors([]);
    warnedSkillNamesRef.current = [];
    onClose();
  };

  const defaultFormValues: SettingsFormData =
    jobTitle && jobTitle.trim().length > 0
      ? { ...EMPTY_FORM_STATE, job_titles: [jobTitle.trim()] }
      : EMPTY_FORM_STATE;

  if (!isOpen) {
    return null;
  }

  return (
    <Form<SettingsFormData>
      onSubmit={onSubmit}
      defaultValues={defaultFormValues}
    >
      <CalibrationForm
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        validationErrors={validationErrors}
        isValidating={isPending}
      />
    </Form>
  );
};

export default CalibrationModal;
